"use client"

import { subscribeNewsletter } from "@lib/data/newsletter"
import Modal from "@modules/common/components/modal"
import { Button, Input, Text } from "@modules/common/components/ui"
import { FormEvent, useEffect, useState } from "react"

const SESSION_KEY = "_tnp_exit_intent_shown"

type Status = "idle" | "loading" | "success" | "error"

// Desktop-only exit-intent capture. Fires once per session (sessionStorage
// hard cap) when the cursor leaves through the top of the viewport and the
// cart has items. No exit intent on mobile.
export default function ExitIntentModal({ itemCount }: { itemCount: number }) {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState<Status>("idle")

  useEffect(() => {
    if (itemCount <= 0) {
      return
    }

    if (window.sessionStorage.getItem(SESSION_KEY)) {
      return
    }

    // Desktop only — small: breakpoint is 1024px in this project.
    if (!window.matchMedia("(min-width: 1024px)").matches) {
      return
    }

    const onMouseLeave = (event: MouseEvent) => {
      if (event.clientY > 0) {
        return
      }

      if (window.sessionStorage.getItem(SESSION_KEY)) {
        return
      }

      window.sessionStorage.setItem(SESSION_KEY, "1")
      setOpen(true)
    }

    document.documentElement.addEventListener("mouseleave", onMouseLeave)

    return () => {
      document.documentElement.removeEventListener("mouseleave", onMouseLeave)
    }
  }, [itemCount])

  const close = () => setOpen(false)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!email || status === "loading") {
      return
    }

    setStatus("loading")

    const { subscribed } = await subscribeNewsletter(email)

    setStatus(subscribed ? "success" : "error")
  }

  const itemLabel = itemCount === 1 ? "1 part" : `${itemCount} parts`

  return (
    <>
      <span data-testid="exit-intent-root" hidden />
      <Modal isOpen={open} close={close} size="small" data-testid="exit-intent-modal">
        <Modal.Title>Before you go — don&rsquo;t lose your build</Modal.Title>
        <div className="flex flex-col gap-y-4 pt-4">
          {status === "success" ? (
            <Text
              role="status"
              className="txt-medium text-ui-fg-base"
              data-testid="exit-intent-success"
            >
              You&rsquo;re all set — we&rsquo;ll email your cart shortly.
            </Text>
          ) : (
            <>
              <Text className="txt-medium text-ui-fg-subtle">
                You have {itemLabel} in your cart. Your parts are verified to
                fit. Save your cart and we&rsquo;ll email it to you.
              </Text>
              <Text className="txt-small text-ui-fg-subtle">
                Plus get launch offers and restock alerts.
              </Text>
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-y-2"
                data-testid="exit-intent-form"
              >
                <div className="flex gap-x-2">
                  <Input
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    aria-label="Email address to save your cart"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      if (status === "error") {
                        setStatus("idle")
                      }
                    }}
                    className="h-10 text-sm"
                    data-testid="exit-intent-email-input"
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    className="h-10 shrink-0"
                    isLoading={status === "loading"}
                    data-testid="exit-intent-submit-button"
                  >
                    Save my cart
                  </Button>
                </div>
                {status === "error" && (
                  <Text
                    role="alert"
                    className="txt-compact-xsmall text-red-600"
                    data-testid="exit-intent-error"
                  >
                    Something went wrong — please try again
                  </Text>
                )}
              </form>
            </>
          )}
          <button
            type="button"
            onClick={close}
            className="self-start text-sm text-ui-fg-subtle underline hover:text-ui-fg-base"
            data-testid="exit-intent-dismiss"
          >
            I&rsquo;m still shopping
          </button>
        </div>
      </Modal>
    </>
  )
}
